import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { EAIChatPageId } from 'types/ai';
import { EPPAStep } from 'types/ppaOnboarding';
import useGetPPAServiceDetail from 'hooks/useGetPPAServiceDetail';
import { getStep, saveKlarityServiceSpecialty, savePPAPaymentMethod, saveStep } from 'api/ppaOnboarding';
import { EInvitedProviderStep, INVITED_STEP_ROUTE, isInvitedProviderStep } from '../steps';
import { saveProviderKlarityService } from 'api/klarityService';
import SetupService2 from 'pages/PPAOnboardingSteps/components/Steps/SetupService/Step2';
import SetupService3, { IStep3Ref } from 'pages/PPAOnboardingSteps/components/Steps/SetupService/Step3';
import SetupService4 from 'pages/PPAOnboardingSteps/components/Steps/SetupService/Step4';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { EInvitedOnboardingStep } from '../types';
import s from './s.module.less';

// Sub-steps within "Set up services". Reuse PPA Step2/3/4 components.
type SubStep = EPPAStep.STEP_6 | EPPAStep.STEP_7 | EPPAStep.STEP_8;

const SUB_STEP_ORDER: SubStep[] = [EPPAStep.STEP_6, EPPAStep.STEP_7, EPPAStep.STEP_8];

const FORM_IDS: Record<SubStep, string> = {
    [EPPAStep.STEP_6]: 'invited-specialty',
    [EPPAStep.STEP_7]: 'invited-payment',
    [EPPAStep.STEP_8]: 'invited-service-template',
};

const AI_CHAT_BY_SUB_STEP: Record<SubStep, EAIChatPageId> = {
    [EPPAStep.STEP_6]: EAIChatPageId.INVITED_PROVIDER_SETUP_SERVICES_SPECIALTY,
    [EPPAStep.STEP_7]: EAIChatPageId.INVITED_PROVIDER_SETUP_SERVICES_PAYMENT,
    [EPPAStep.STEP_8]: EAIChatPageId.INVITED_PROVIDER_SETUP_SERVICES_TEMPLATE,
};

const SetupServicesPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const cameFromBack = (location.state as { fromNext?: boolean } | null)?.fromNext === false;
    const [serviceDetail, , , refetchServiceDetail] = useGetPPAServiceDetail();
    const [subStep, setSubStep] = useState<SubStep>(cameFromBack ? EPPAStep.STEP_8 : EPPAStep.STEP_6);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [nextDisabled, setNextDisabled] = useState(false);
    const step3Ref = useRef<IStep3Ref>(null);
    const [paymentSelected, setPaymentSelected] = useState<string>('');

    useEffect(() => {
        setNextDisabled(false);
    }, [subStep]);

    // Seed the payment selection from saved data so the footer "Next" works on a pre-filled answer.
    useEffect(() => {
        if (serviceDetail?.supportPayType) {
            setPaymentSelected((prev) => prev || serviceDetail.supportPayType || '');
        }
    }, [serviceDetail?.supportPayType]);

    useEffect(() => {
        if (cameFromBack) return undefined;
        let cancelled = false;
        (async () => {
            const res = await getStep();
            const saved = res?.data?.data?.currentStep;
            if (cancelled || !isInvitedProviderStep(saved)) return;
            if (saved === EInvitedProviderStep.SETUP_SERVICES_SPECIALTY) return;
            if (saved === EInvitedProviderStep.SETUP_SERVICES_PAYMENT) {
                setSubStep(EPPAStep.STEP_7);
                return;
            }
            if (saved === EInvitedProviderStep.SETUP_SERVICES_TEMPLATE) {
                setSubStep(EPPAStep.STEP_8);
                return;
            }
            const target = INVITED_STEP_ROUTE[saved];
            if (target && target !== PATH.INVITED_PROVIDER_ONBOARDING_SERVICES) {
                navigate(target, { replace: true });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const goToNextSubStep = useCallback(async () => {
        const idx = SUB_STEP_ORDER.indexOf(subStep);
        if (idx < SUB_STEP_ORDER.length - 1) {
            const nextSubStep = SUB_STEP_ORDER[idx + 1];
            const nextSemanticStep =
                nextSubStep === EPPAStep.STEP_7
                    ? EInvitedProviderStep.SETUP_SERVICES_PAYMENT
                    : EInvitedProviderStep.SETUP_SERVICES_TEMPLATE;
            await saveStep(nextSemanticStep);
            setSubStep(nextSubStep);
            refetchServiceDetail?.();
        } else {
            await saveStep(EInvitedProviderStep.MARKETING_PERSONAL_ID);
            navigate(PATH.INVITED_PROVIDER_ONBOARDING_MARKETING_PROFILE);
        }
    }, [subStep, navigate, refetchServiceDetail]);

    const handleNext = useCallback(
        async (data: any, stepVal: EPPAStep) => {
            try {
                setSubmitLoading(true);
                let res: any = null;
                if (stepVal === EPPAStep.STEP_6) {
                    res = await saveKlarityServiceSpecialty(data);
                } else if (stepVal === EPPAStep.STEP_7) {
                    res = await savePPAPaymentMethod(data.supportPayType);
                } else {
                    res = await saveProviderKlarityService(
                        data.providerKlarityService,
                        data.disableIds || [],
                        data.conditions || [],
                    );
                }
                if (res && !res.error) {
                    goToNextSubStep();
                } else {
                    message.error(res?.error);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setSubmitLoading(false);
            }
        },
        [goToNextSubStep],
    );

    const handleBack = useCallback(() => {
        const idx = SUB_STEP_ORDER.indexOf(subStep);
        if (idx > 0) {
            setSubStep(SUB_STEP_ORDER[idx - 1]);
        } else {
            navigate(PATH.INVITED_PROVIDER_ONBOARDING_CREATE_PROFILE, { state: { fromNext: false } });
        }
    }, [subStep, navigate]);

    const handleSkip = useCallback(async () => {
        await saveStep(EInvitedProviderStep.MARKETING_PERSONAL_ID);
        navigate(PATH.INVITED_PROVIDER_ONBOARDING_MARKETING_PROFILE);
    }, [navigate]);

    const formId = FORM_IDS[subStep];

    const contentRender = useMemo(() => {
        if (subStep === EPPAStep.STEP_6) {
            return (
                <SetupService2
                    serviceDetail={serviceDetail}
                    formId={formId}
                    onNext={handleNext}
                    setNextButtonDisabled={setNextDisabled}
                    wrapClassName={s.specialtyWrap}
                />
            );
        }
        if (subStep === EPPAStep.STEP_7) {
            return (
                <div className={s.subStepWrap}>
                    <SetupService3
                        ref={step3Ref}
                        serviceDetail={serviceDetail}
                        onNext={handleNext}
                        onSelectionChange={setPaymentSelected}
                    />
                </div>
            );
        }
        return (
            <div className={s.subStepWrap}>
                <SetupService4 serviceDetail={serviceDetail} formId={formId} onNext={handleNext} />
            </div>
        );
    }, [subStep, serviceDetail, formId, handleNext]);

    const isPaymentStep = subStep === EPPAStep.STEP_7;
    const footerRender = (
        <Footer
            showBack
            onBack={handleBack}
            showNext
            // Step 7 (payment) has no form — Next replays the selected card click (incl. the insurance modal).
            nextFormId={isPaymentStep ? undefined : formId}
            onNext={isPaymentStep ? () => step3Ref.current?.submit() : undefined}
            nextLoading={submitLoading}
            nextDisabled={isPaymentStep ? !paymentSelected : nextDisabled}
            onSkip={subStep === EPPAStep.STEP_6 ? undefined : handleSkip}
        />
    );

    return (
        <Layout
            step={EInvitedOnboardingStep.SETUP_SERVICES}
            aiChatPageId={AI_CHAT_BY_SUB_STEP[subStep]}
            footer={footerRender}
        >
            {contentRender}
        </Layout>
    );
};

export default SetupServicesPage;
